import Joi from 'joi';

import trainTime from './lib/TrainTime';
import { translate as t } from './lib/I18n';
import { Brands, Validation } from './constants/AppConstants';
import config from './config';
import _ from 'lodash';

const validators = {

    required (requiredMessage = 'alertmessage.required') {
        return Joi.any().invalid(null, '').required().messages({
            'any.invalid': t(requiredMessage),
            'any.required': t(requiredMessage),
        });
    },

    optional () {
        return Joi.any().optional();
    },

    name (min = 1, max = 19) {
        return Joi.string().regex(Validation.lettersAndSpaces).trim().required().min(min).max(max).messages({
            'any.required': t('alertmessage.required'),
            'string.empty': t('alertmessage.empty'),
            'string.base': t('alertmessage.string.min', { num: min }),
            'string.min': t('alertmessage.string.min', { num: min }),
            'string.max': t('alertmessage.string.max', { num: max }),
            'string.pattern.base': t('alertmessage.badName'),
        });
    },

    requiredString (min = 1, max, msg = 'alertmessage.required') {
        let validator = Joi.string().trim().required().min(min);
        let messages = {
            'any.required': t(msg),
            'string.empty': min === 1 ? t(msg) : t('alertmessage.string.min', { num: min }),
            'string.base': t(msg),
            'string.min': min === 1 ? t(msg) : t('alertmessage.string.min', { num: min }),
        };

        if (_.isNumber(max)) {
            validator = validator.max(max);
            messages['string.max'] = t('alertmessage.string.max', { num: max });
        }

        return validator.messages(messages);
    },

    voucherAmount (maxAmount) {
        return Joi.number().min(0).max(maxAmount).required().messages({
            'number.base': t('voucher.error.number.base'),
            'number.min': t('voucher.error.number.min'),
            'number.max': t('voucher.error.number.max', { maxAmount: maxAmount.toFixed(2) }),
        });
    },

    price () {
        return Joi.number().min(0).required().messages({
            'number.base': t('alertmessage.price'),
        });
    },

    cardHolderName () {
        return Joi.string().regex(Validation.lettersAndSpaces).regex(Validation.cardHolderName).trim().min(3).required().messages({
            'any.required': t('alertmessage.badCardHolder'),
            'string.empty': t('alertmessage.badCardHolder'),
            'string.base': t('alertmessage.badCardHolder'),
            'string.min': t('alertmessage.badCardHolder'),
            'string.pattern.base': t('alertmessage.badCardHolder'),
        });
    },

    photocardHolder (message = t('alertmessage.badPhotocardFirstName')) {
        return Joi.string().regex(Validation.photocardHolder).trim().required().messages({
            'any.required': message,
            'string.empty': message,
            'string.base': message,
            'string.min': message,
            'string.pattern.base': message,
        });
    },

    cardNumber () {
        return Joi.string().trim().creditCard().required().messages({
            'string.base': t('alertmessage.badCardNumber'),
            'string.creditCard': t('alertmessage.badCardNumber'),
        });
    },

    isrn () {
        return Joi.string().trim().regex(Validation.keySmartcardNumber).required().messages({
            'string.empty': t('alertmessage.smartcard', { fieldName: t('account.smartcard.text.label') }),
            'string.base': t('alertmessage.smartcard', { fieldName: t('account.smartcard.text.label') }),
            'string.pattern.base': t('alertmessage.smartcard', { fieldName: t('account.smartcard.text.label') }),
        });
    },

    expiryMonth () {
        return Joi.number().min(1).max(12).required().messages({
            'number.base': t('alertmessage.badExpiryMonth'),
            'number.min': t('alertmessage.badExpiryMonth'),
            'number.max': t('alertmessage.badExpiryMonth'),
        });
    },

    expiryYear () {
        return Joi.number().min(trainTime().year()).required().messages({
            'number.base': t('alertmessage.badExpiryYear'),
            'number.min': t('alertmessage.badExpiryYear'),
            'number.max': t('alertmessage.badExpiryYear'),
        });
    },

    isAdult () {
        return Joi.boolean().required().messages({
            'boolean.base': 'test',
        });
    },

    cv2 () {
        return Joi.string().trim().regex(Validation.cv2).required().messages({
            'string.empty': t('alertmessage.badCvv'),
            'string.base': t('alertmessage.badCvv'),
            'string.pattern.base': t('alertmessage.badCvv'),
        });
    },

    addressLine () {
        return Joi.string().trim().min(1).max(50).messages({
            'string.empty': t('alertmessage.badAddressLine'),
            'string.base': t('alertmessage.badAddressLine'),
            'string.min': t('alertmessage.badAddressLine'),
            'string.max': t('alertmessage.string.max', { num: 50 }),
        });
    },

    textMaxLength (maxLength = 50) {
        return Joi.string().trim().max(maxLength).allow(null, '').messages({
            'string.base': t('alertmessage.string.max', { num: maxLength }),
            'string.max': t('alertmessage.string.max', { num: maxLength }),
        });
    },

    townCity () {
        return Joi.string().trim().min(2).max(50).required().messages({
            'string.empty': t('alertmessage.badTownCity'),
            'string.base': t('alertmessage.badTownCity'),
            'string.min': t('alertmessage.badTownCity'),
            'string.max': t('alertmessage.string.max', { num: 50 }),
        });
    },

    postcode () {
        return Joi.string().trim().regex(Validation.postcode).required().messages({
            'string.empty': t('alertmessage.badPostCode'),
            'string.base': t('alertmessage.badPostCode'),
            'string.pattern.base': t('alertmessage.badPostCode'),
        });
    },

    country () {
        return Joi.string().trim().regex(Validation.countryCode).required().messages({
            'string.empty': t('alertmessage.badCountry'),
            'string.base': t('alertmessage.badCountry'),
            'string.pattern.base': t('alertmessage.badCountry'),
        });
    },

    email () {
        return Joi.string().trim().email({ tlds: { allow: false } }).required().messages({
            'string.empty': t('alertmessage.badEmail'),
            'string.base': t('alertmessage.badEmail'),
            'string.email': t('alertmessage.badEmail'),
        });
    },

    confirmEmail () {
        const brand = config.get('brand');
        const isGTR = _.values(Brands).includes(brand);

        // .empty('') converts '' to undefined before .valid() runs, so an untouched
        // field shows "required" (not "fields do not match") on both GTR and non-GTR.
        const baseValidator = Joi.string().trim().empty('').valid(Joi.ref('email')).messages({
            'any.only': t('alertmessage.fieldsNoMatch', { field: 'Email' }),
            'any.required': t('alertmessage.required'),
            'string.base': t('alertmessage.required'),
        });

        if (isGTR) {
            return baseValidator.required();
        } else {
            return baseValidator.optional();
        }
    },

    phone (maxLength = 20) {
        return Joi.string().trim().regex(Validation.telephone).max(maxLength).messages({
            'string.empty': t('alertmessage.badContactNumber'),
            'string.base': t('alertmessage.badContactNumber'),
            'string.max': t('alertmessage.string.max', { num: maxLength }),
            'string.pattern.base': t('alertmessage.badContactNumber'),
        });
    },

    passwordEntry () {
        return this.requiredString().messages({
            'string.base': t('alertmessage.login.badPassword'),
            'string.min': t('alertmessage.login.badPassword'),
        });
    },

    passwordCreate () {
        return Joi.string()
            .trim()
            .min(config.get('minPasswordLength'))
            .pattern(/[a-z]/, { name: 'lowercase characters' })
            .pattern(/[A-Z]/, { name: 'uppercase characters' })
            .pattern(/\d/, { name: 'digits' })
            .messages({
                'string.empty': t('alertmessage.badPassword'),
                'string.base': t('alertmessage.badPassword'),
                'string.min': t('alertmessage.badPassword.tooShort'),
                'string.pattern.base': t('alertmessage.badPassword'),
                'string.pattern.name': 'One or more {{#name}} required',
            });
    },

    title (message = t('alertmessage.choose')) {
        return Joi.any().valid(...(config.get('customerTitles') || [])).messages({
            'any.only': message,
        });
    },

    regex (regex, fieldName = t('alertmessage.regex.fieldName'), alertmessage = 'alertmessage.regex') {
        return Joi.string().regex(regex).messages({
            'any.required': t('alertmessage.required'),
            'string.base': t(alertmessage, { fieldName }),
            'string.pattern.base': t(alertmessage, { fieldName }),
        });
    },

    allowOnly (list) {
        return Joi.string().valid(...list).messages({
            'any.only': t('alertmessage.choose'),
            'any.required': t('alertmessage.required'),
            'string.empty': t('alertmessage.required'),
            'string.base': t('alertmessage.required'),
        });
    },

    true: () => validators.boolean(true, 'alertmessage.required'),

    boolean (value = false, message = 'alertmessage.required') {
        return Joi.any().valid(!!value).messages({
            'any.only': t(message),
            'any.required': t(message),
        });
    },

    isOver16: () => {
        return Joi.any().valid(true).messages({
            'any.only': t('alertmessage.date.over16'),
            'any.required': t('alertmessage.date.over16'),
        });
    },

    sortCode: () => {
        return validators.regex(Validation.sortCode).required().messages({
            'string.empty': t('alertmessage.badSortCode'),
            'string.base': t('alertmessage.badSortCode'),
            'string.pattern.base': t('alertmessage.badSortCode'),
        });
    },

    accountNumber: () => {
        return validators.regex(Validation.accountNumber).required().messages({
            'string.empty': t('alertmessage.badAccountNumber'),
            'string.base': t('alertmessage.badAccountNumber'),
            'string.pattern.base': t('alertmessage.badAccountNumber'),
        });
    },

    railcardsNumber (passengersNumberRef) {
        return Joi.number().min(0).max(Joi.ref(passengersNumberRef)).messages({
            'number.max': t('alertmessage.form.moreRailcardsThanPassengers'),
        });
    },

    collectionLocation (required = false) {
        return required ? Joi.any().invalid(null, '').required().messages({
            'any.invalid': t('alertmessage.required'),
            'any.required': t('alertmessage.required'),
        }) : Joi.any().optional();
    }
};


export default validators;
